import React, { forwardRef, useEffect, useMemo, useRef } from "react";

import { Checkbox, Icon } from "@braze/beacon-core";

import { assertElementIsActionable } from "@lib/agenticAi/assertElementIsActionable";
import { toggleCheckboxDescription, toggleCheckboxParameters } from "@lib/agenticAi/sharedToolContracts";

import useI18nNamespace from "@services/useI18nNamespace";
import { useOperatorEntityId } from "@services/useOperatorEntityId";
import { useRegisterChangeTool } from "@services/useRegisterChangeTool";

import { DiffDisplay } from "@components/AgenticAi/components/DiffDisplay";
import { useMergedRef } from "@components/AgenticAi/hooks/useMergedRef";
import { useBrazeCheckboxGroupContext } from "@components/BrazeCheckboxGroup/BrazeCheckboxGroupContext";

import type { BrazeCheckboxProps } from "./types";

const getVisibleText = (node: React.ReactNode): string => {
  if (typeof node === "string" || typeof node === "number") {
    return String(node);
  }
  if (!React.isValidElement(node)) {
    return React.Children.toArray(node)
      .map((child) => getVisibleText(child))
      .join("");
  }
  return getVisibleText(node.props.children);
};

export const BrazeCheckbox = forwardRef<HTMLInputElement, BrazeCheckboxProps>(
  ({ onChange, operatorLabel: label, operatorDisabledReason, disabled, checked, readOnly, ...props }, ref) => {
    const { t } = useI18nNamespace("braze-checkbox");
    const registerChangeTool = useRegisterChangeTool({ operatorDisabledReason });
    const id = useOperatorEntityId("braze-checkbox");
    const checkboxGroupContext = useBrazeCheckboxGroupContext();
    const isInCheckboxGroup = checkboxGroupContext !== null;
    // Internal identity for this mounted checkbox; labels and values can collide or change.
    const groupRegistrationId = useRef(Symbol("braze-checkbox-group-option"));

    // Dispatch a native click on Beacon's real input so Formik and other handlers receive a
    // complete event with the input's current name, id, value, and checked state.
    const { elementRef, setRef } = useMergedRef<HTMLInputElement>(ref);

    const checkedRef = useRef(checked);
    checkedRef.current = checked;
    const indeterminateRef = useRef(Boolean(props.indeterminate));
    indeterminateRef.current = Boolean(props.indeterminate);

    useEffect(() => {
      if (!checkboxGroupContext) {
        return;
      }

      return checkboxGroupContext.registerOption(groupRegistrationId.current, {
        key: label || getVisibleText(props.children) || String(props.value ?? ""),
        checked: Boolean(checked),
        indeterminate: Boolean(props.indeterminate),
        value: props.value,
        name: props.name,
        id: props.id,
        disabled: Boolean(disabled),
        readOnly: Boolean(readOnly),
        onChange,
        elementRef,
      });
    }, [
      checkboxGroupContext,
      label,
      checked,
      props.children,
      props.indeterminate,
      props.value,
      props.name,
      props.id,
      disabled,
      readOnly,
      onChange,
      elementRef,
    ]);

    useEffect(() => {
      if (isInCheckboxGroup || disabled || readOnly) {
        return;
      }

      const cleanup = registerChangeTool({
        toolEntityId: id,
        name: "toggle_checkbox",
        description: toggleCheckboxDescription,
        parameters: toggleCheckboxParameters,
        callback: (args) => {
          const element = elementRef.current;
          assertElementIsActionable(
            element,
            { disabled: element?.disabled ?? false, readOnly: element?.readOnly ?? false },
            `${label} checkbox`,
            "toggled"
          );

          const current = element.checked;
          const isIndeterminate = indeterminateRef.current;
          let next: boolean;
          if (typeof args.to_checked === "boolean") {
            if (args.to_checked === current && !isIndeterminate) {
              return args.to_checked ? "Already on — no change made." : "Already off — no change made.";
            }
            next = args.to_checked;
          } else {
            next = isIndeterminate ? true : !current;
          }
          // A native indeterminate checkbox may have the same checked value as the
          // requested state. Seed the opposite value so the real click still emits
          // the complete change event expected by controlled form fields.
          if (isIndeterminate && element.checked === next) {
            element.checked = !next;
          }
          element.click();

          return next ? "Checkbox was turned on." : "Checkbox was turned off.";
        },
        options: {
          requiredSkill: "page-controls",
          toolGroup: "Agentic Creation & Editing",
          labels: {
            ready: t("toggle-checkbox-ready", "Preparing to toggle"),
            running: t("toggle-checkbox-running", "Toggling"),
            complete: t("toggle-checkbox-complete", "Toggled"),
            failed: t("toggle-checkbox-failed", "Failed to toggle"),
            stopped: t("toggle-checkbox-stopped", "Stopped toggling"),
          },
          requiresApproval: "yes",
          argumentsSummaryText: (args) => {
            if (args && typeof args === "object" && "to_checked" in args && typeof args.to_checked === "boolean") {
              return args.to_checked ? `${label} (on)` : `${label} (off)`;
            }
            return label;
          },
          renderApproval: ({ args }) => {
            const current = Boolean(checkedRef.current);
            const isPartiallyEnabled = Boolean(indeterminateRef.current);
            const next =
              typeof args.to_checked === "boolean" ? args.to_checked : isPartiallyEnabled ? true : !current;
            const enabledText = t("enabled", "Enabled");
            const disabledText = t("disabled", "Disabled");
            const partialText = t("partially-enabled", "Partially enabled");
            const original = isPartiallyEnabled ? partialText : current ? enabledText : disabledText;

            return <DiffDisplay title={label} original={original} modified={next ? enabledText : disabledText} />;
          },
          toolIcon: <Icon name="check-square" />,
        },
      });

      return () => {
        cleanup();
      };
    }, [registerChangeTool, id, label, t, disabled, readOnly, elementRef, isInCheckboxGroup]);

    const operatorDescription = useMemo(() => {
      const labelElement = document.createElement("label");
      const input = document.createElement("input");
      input.type = "checkbox";
      input.id = id;
      if (checked) {
        input.setAttribute("checked", "checked");
      }
      if (disabled) {
        input.disabled = true;
      }
      if (readOnly) {
        input.readOnly = true;
      }
      labelElement.appendChild(input);
      labelElement.appendChild(document.createTextNode(label));
      return labelElement.outerHTML;
    }, [id, checked, disabled, label, readOnly]);

    return (
      <Checkbox
        aria-label={label}
        {...props}
        onChange={onChange}
        data-operator-description={isInCheckboxGroup ? undefined : operatorDescription}
        data-checked={checked}
        disabled={disabled}
        readOnly={readOnly}
        checked={checked}
        ref={setRef}
      />
    );
  }
);
